import { Select } from 'components/v2/Select'
import { ReactElement } from 'react'
import { FormattedMessage } from 'react-intl'
import { useField } from 'formik'
import { useQuery } from '@apollo/client'
import { GET_BUSINESS_CLIENT_KREDITS_SETTINGS } from 'features/Kredits/queries-legacy'
import { intToLetter } from 'utils/number'
import { isEmpty } from 'lodash'
import { Label } from 'components/v2/Label'
import { GTKreditCategory, GTKreditsSettings } from 'gql/types.generated'

const defaultCategories = [
  { value: 1, label: 'A' },
  { value: 2, label: 'B' },
  { value: 3, label: 'C' },
  { value: 4, label: 'D' },
  { value: 5, label: 'E' },
  { value: 6, label: 'F' }
]

type SelectKreditsCategoryProps = {
  id?: string
  name: string
  excludedOptions?: number[]
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void
  disabled?: boolean
  businessClientId?: string
  businessSiteId?: string
  label?: ReactElement
  isRequiredSiteOrClient?: boolean
}

interface IBusinessClientKreditsSettingsOutput {
  businessClient: {
    kreditsSettings: GTKreditsSettings
  }
}

const SelectKreditsCategory = ({
  id,
  name,
  excludedOptions,
  onChange,
  businessClientId,
  businessSiteId,
  isRequiredSiteOrClient,
  disabled,
  label
}: SelectKreditsCategoryProps): ReactElement => {
  const [field, meta, helpers] = useField(name)

  const { data: businessClientKreditsSettingsData } = useQuery<IBusinessClientKreditsSettingsOutput>(
    GET_BUSINESS_CLIENT_KREDITS_SETTINGS,
    {
      variables: { id: businessClientId },
      skip: isEmpty(businessClientId)
    }
  )

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const { value } = event.target
    helpers.setValue(value)
  }

  type Option = { value: number; label: string }

  const mapCategoriesToOptions = (categories: GTKreditCategory[]): Option[] =>
    categories.map((category) => ({
      value: Number(category.name),
      label: intToLetter(Number(category.name))
    }))

  const filterOptions = (options: Option[], excludedOptions?: number[]): Option[] =>
    excludedOptions ? options.filter((option) => !excludedOptions.includes(option.value)) : options

  const getBusinessClientKreditsCategories = (): Option[] => {
    const categories = businessClientKreditsSettingsData?.businessClient?.kreditsSettings?.categories ?? []
    return mapCategoriesToOptions(categories)
  }

  const getBusinessSiteKreditsCategories = (): Option[] => {
    const categories =
      businessClientKreditsSettingsData?.businessClient?.kreditsSettings?.businessSites?.find(
        (site) => site.id === businessSiteId
      )?.categories ?? []
    return mapCategoriesToOptions(categories)
  }

  const getOptionsToRender = (): Option[] => {
    const siteCategories = getBusinessSiteKreditsCategories()
    const clientCategories = getBusinessClientKreditsCategories()

    if (!isEmpty(siteCategories)) {
      return filterOptions(siteCategories, excludedOptions)
    }

    if (!isEmpty(clientCategories)) {
      return filterOptions(clientCategories, excludedOptions)
    }

    if (isRequiredSiteOrClient) {
      return []
    }

    return filterOptions(defaultCategories, excludedOptions)
  }

  return (
    <div>
      {label ? <Label>{label}</Label> : null}
      <Select
        {...field}
        id={id}
        onChange={onChange ?? handleChange}
        errors={meta.error}
        touched={meta.touched}
        disabled={disabled}
      >
        <option value="">
          <FormattedMessage id="kreditCategory.placeholder" defaultMessage="Select a category" />
        </option>
        {getOptionsToRender().map((option) => (
          <option key={option.value} value={option.value}>
            <FormattedMessage
              id="kreditCategory.label"
              defaultMessage="Category {name}"
              values={{ name: option.label }}
            />
          </option>
        ))}
      </Select>
    </div>
  )
}

export default SelectKreditsCategory
